import React, { useEffect, useState } from 'react';
import CustomAlert from '../components/alert/commonAlert';
import { clearLogoutError, reset, syncTokens } from '../slices/userSlice';
import { clearLocalStorage } from '../common/utils';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import storage from '../common/localStorage';
import { postRefreshToken } from '../api/services/authService';


let activityTimer: ReturnType<typeof setTimeout>;
let warningTimer: ReturnType<typeof setTimeout>;
let countdownInterval: ReturnType<typeof setInterval> | null = null;

type UseSessionTimeoutProps = {
  onTimeout: () => void;
  setShowWarning: React.Dispatch<React.SetStateAction<boolean>>;
  setCountdown: React.Dispatch<React.SetStateAction<number | null>>;
  isLoggedIn: boolean;
  resetTimer: () => void; // Function to reset the timer
  activityTimeout: number;
  activityWarningTimeout: number;
  countdownTime: number;
};

const useSessionTimeout = ({
  onTimeout,
  setShowWarning,
  setCountdown,
  isLoggedIn,
  resetTimer,
  activityTimeout,
  activityWarningTimeout,
  countdownTime
}: UseSessionTimeoutProps) => {

  

  useEffect(() => {
    const resetTimerFn = () => {
      clearTimeout(activityTimer);
      clearTimeout(warningTimer);
      if (countdownInterval) clearInterval(countdownInterval); // Clear the countdown interval

      if (isLoggedIn) {
        activityTimer = setTimeout(onTimeout, activityTimeout);
        warningTimer = setTimeout(() => {
          setShowWarning(true);
          setCountdown(countdownTime); // Start countdown from 5 minuts
        }, activityWarningTimeout);
      }
    };

    resetTimerFn(); // Initialize the timer when component mounts or login state changes

    // Set the function to reset the timer
    resetTimer(); // Call the resetTimer to handle resetting in parent component

    return () => {
      clearTimeout(activityTimer);
      clearTimeout(warningTimer);
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [onTimeout, setShowWarning, setCountdown, isLoggedIn, resetTimer]);
};

const UserSessionTimeout: React.FC = () => {
  const [isLoggedOut, setIsLoggedOut] = useState<boolean>(false);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [showSessionExpired, setShowSessionExpired] = useState<boolean>(false); // For session expired popup
  const dispatch = useAppDispatch();
  dispatch(clearLogoutError()); // Clear any logout errors
  const localTokens = storage.GetTokens();

  const userSessionTime =  Number(localTokens?.sessionTime) - ( 0.15 );
  const countDownTime = Number(localTokens?.countDownTime);

  const ACTIVITY_TIMEOUT = userSessionTime * 60 * 1000; // 30 minutes timeout for inactivity
  const ACTIVITY_WARNING_TIMEOUT = (userSessionTime - countDownTime) * 60 * 1000; // 25 minutes for activity warning
  const COUNTDOWN_TIME = countDownTime * 60; // 5 minutes countdown for the warning popup 


  const isLoggedIn = useAppSelector((state) => state.user.status === 'authenticated');
  const currentPage = window.location.pathname;

  const handleTimeout = () => {
    setIsLoggedOut(true); // Mark as logged out
    dispatch(reset()); // Proceed with logging out
    dispatch(clearLogoutError()); // Clear any logout errors
    clearLocalStorage(); // Clear local storage
  };

  const formatCountdown = (seconds: number | null): string => {
    if (seconds === null) return '00:00'; // Default value if no countdown
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes < 10 ? '0' + minutes : minutes}:${remainingSeconds < 10 ? '0' + remainingSeconds : remainingSeconds}`;
  };

  // Set up the session timeout hook
  const resetTimer = () => {
    clearTimeout(activityTimer);
    clearTimeout(warningTimer);
    if (countdownInterval) clearInterval(countdownInterval); // Reset countdown interval

    if (isLoggedIn && !showSessionExpired) {
      activityTimer = setTimeout(handleTimeout, ACTIVITY_TIMEOUT);
      warningTimer = setTimeout(() => {
        setShowWarning(true);
        setCountdown(COUNTDOWN_TIME); // Start countdown from 15 seconds
      }, ACTIVITY_WARNING_TIMEOUT);
    }
  };

  useSessionTimeout({
    onTimeout: handleTimeout,
    setShowWarning,
    setCountdown,
    isLoggedIn,
    resetTimer, // Pass the resetTimer function
    activityTimeout: ACTIVITY_TIMEOUT,
    activityWarningTimeout: ACTIVITY_WARNING_TIMEOUT,
    countdownTime: COUNTDOWN_TIME
  });

  useEffect(() => {
    if (showWarning && countdown !== null && countdown > 0 && currentPage !== '/login') {
      countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev === 1) {
            clearInterval(countdownInterval!); // Stop the countdown
            setShowWarning(false);
            setShowSessionExpired(true); // Show the session expired popup
          }
          return prev! - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval!);
    }
  }, [showWarning, countdown, currentPage]);

  const handleContinueSession = () => {
    setShowWarning(false);
    validateToken();
    setCountdown(null);
    resetTimer(); // Restart the timer from the session warning
  };

  const handleSessionExpired = () => {
    setShowWarning(false)
    handleTimeout();
    resetTimer(); // Reset the timer when user acknowledges
    setShowSessionExpired(false);
  };

  const validateToken = async () => {
    try {
      const localTokens = storage.GetTokens();
      const refreshToken = localTokens?.refreshToken;
      if (refreshToken) {
        const response = await postRefreshToken(refreshToken);
        if (response.status === 200) {
          const { token, refreshToken: newRefreshToken, sessionTime, countDownTime } = response.data;
          dispatch(syncTokens({ token, refreshToken: newRefreshToken, sessionTime: sessionTime.toString(), countDownTime: countDownTime.toString() }));
          storage.SetTokens({ token, refreshToken: newRefreshToken, sessionTime: sessionTime.toString(), countDownTime: countDownTime.toString() });
        }
      }
    } catch (error) {
      console.error('Failed to refresh token:', error);
    }
  };


  if (!isLoggedIn) {
    return null;
  }

  return (
    <div>
      {showWarning && currentPage !== '/login' && !showSessionExpired && (
        <CustomAlert
          message={`Your session will expire in ${formatCountdown(countdown)} minutes.`}
          onContinue={handleContinueSession} // User wants to continue session, reset the timer
        />
      )}
      {showSessionExpired && currentPage !== '/login' && (
        <CustomAlert
          message="Your session has expired. Please click OK to login again."
          onClose={handleSessionExpired} // OK button that resets the session and redirects to login page
        />
      )}
    </div>
  );
};

export default UserSessionTimeout;
